// Stable JSON identity, preserving array order and every supplied context field.
export function featureFlagRequestKey(value: any): string {
  return JSON.stringify(value, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
    ? Object.keys(item).sort().reduce<Record<string, any>>((sorted, key) => { sorted[key] = item[key]; return sorted; }, {})
    : item);
}

type Entry<T> = { promise: Promise<T>; resolve(value: T): void; reject(error: any): void; dispatched: boolean; missing(): T };
type Batch<T> = { entries: Map<string, Entry<T>> };

/** Same-turn batching and in-flight sharing only. No completed-result cache or timer. */
export class FeatureFlagRequests<T extends {key: string}> {
  private pending = new Map<string, Entry<T>>();
  private queued = new Map<string, Batch<T>>();

  clear(): void { this.pending.clear(); this.queued.clear(); }

  request(scope: string, key: string, load: (keys: string[]) => Promise<T[]>, missing: () => T,
          forceRefresh = false): Promise<T> {
    const group = featureFlagRequestKey([scope, forceRefresh]);
    const identity = featureFlagRequestKey([group, key]);
    const existing = this.pending.get(identity);
    // A forced check never joins work dispatched before that check.
    if (existing && (!forceRefresh || !existing.dispatched)) { return existing.promise; }
    let resolve!: (value: T) => void;
    let reject!: (error: any) => void;
    const promise = new Promise<T>((yes, no) => { resolve = yes; reject = no; });
    const entry: Entry<T> = {promise, resolve, reject, dispatched: false, missing};
    this.pending.set(identity, entry);
    let batch = this.queued.get(group);
    if (!batch) {
      batch = {entries: new Map()};
      this.queued.set(group, batch);
      const currentBatch = batch;
      void Promise.resolve().then(async () => {
        if (this.queued.get(group) === currentBatch) { this.queued.delete(group); }
        currentBatch.entries.forEach(item => { item.dispatched = true; });
        try {
          const results = await load(Array.from(currentBatch.entries.keys()));
          const byKey = new Map(results.map(result => [result.key, result]));
          currentBatch.entries.forEach((item, flagKey) => {
            item.resolve(byKey.get(flagKey) || item.missing());
          });
        } catch (error) {
          currentBatch.entries.forEach(item => item.reject(error));
        } finally {
          currentBatch.entries.forEach((item, flagKey) => {
            const id = featureFlagRequestKey([group, flagKey]);
            if (this.pending.get(id) === item) { this.pending.delete(id); }
          });
        }
      });
    }
    batch.entries.set(key, entry);
    return entry.promise;
  }
}
